import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Card, CardContent, Typography, Stack } from "@mui/material";
import { ErrorOutline as ErrorIcon, Refresh as RefreshIcon, Dashboard as DashboardIcon } from "@mui/icons-material";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught application error:", error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/dashboard";
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.50",
            p: 3,
          }}
        >
          <Card sx={{ maxWidth: 520, width: "100%", boxShadow: 3, borderRadius: 2 }}>
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <ErrorIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Something went wrong
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                An unexpected error occurred while rendering this view. Your session and data are secure.
              </Typography>
              {this.state.error && (
                <Box
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    textAlign: "left",
                    maxHeight: 120,
                    overflow: "auto",
                  }}
                >
                  <Typography variant="caption" sx={{ fontFamily: "monospace", color: "error.dark" }}>
                    {this.state.error.message || String(this.state.error)}
                  </Typography>
                </Box>
              )}
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="outlined"
                  startIcon={<DashboardIcon />}
                  onClick={this.handleGoHome}
                >
                  Dashboard
                </Button>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleReset}
                >
                  Reload Page
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
